import React, { useEffect, useRef, useState, useCallback, useMemo, useId } from 'react';
import { X, Loader, AlertCircle, ChevronUp, ChevronDown, FileText } from 'lucide-react';
import { pdfjsLib } from '../../../lib/pdfjsSetup';
import { announce } from '../../../lib/a11y';
import {
  resolveFilerPdfPath,
  downloadPdfBytes,
  CitationRef,
} from '../../../lib/commentCitations';

interface DocViewerPanelProps {
  docket: string;
  citation: CitationRef | null;
  segments?: string[];
  onClose: () => void;
}

interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

// Stable empty array so memoized PdfPages aren't re-rendered for pages with
// no quotes to highlight.
const EMPTY_QUOTES: string[] = [];

function normalize(s: string): string {
  return (s || '')
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/­/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Item indices spanning a (possibly ellipsis-joined, lightly-reworded)
 * quote within a page's text items, or null. Returns ORIGINAL indices.
 *
 * pdf.js emits text items in content-stream order, which isn't always
 * reading order (it can scramble across lines/columns/footnotes, and even
 * differs between pdf.js builds). We sort items into reading order
 * (top→bottom, left→right) before matching so a quote stays contiguous,
 * then map matches back to original indices for rect computation. */
function matchItems(rawItems: any[], quote: string): number[] | null {
  const order = rawItems.map((it, i) => ({
    i,
    x: (it.transform && it.transform[4]) || 0,
    y: (it.transform && it.transform[5]) || 0,
  }));
  order.sort((a, b) => {
    const dy = b.y - a.y; // higher y = higher on the page = earlier
    if (Math.abs(dy) > 3) return dy;
    return a.x - b.x;
  });
  const items = order.map((o) => rawItems[o.i]);
  const orig = order.map((o) => o.i);

  // Whitespace-INSENSITIVE matching: some PDFs (and some pdf.js builds) split
  // words mid-glyph ("a ccess", "i ntroduction") or across items, so any
  // space-aware match is fragile. We concatenate all item text with no
  // separators and match against a likewise spaceless quote, tracking each
  // item's character offset so we can map a hit back to items.
  const sp = (s: string) => normalize(s).replace(/\s+/g, '');
  const parts = items.map((it) => sp(it.str));
  const starts: number[] = [];
  let joined = '';
  parts.forEach((t) => {
    starts.push(joined.length);
    joined += t;
  });

  const findNeedle = (needle: string): number[] | null => {
    if (needle.length < 12) return null;
    const idx = joined.indexOf(needle);
    if (idx < 0) return null;
    const end = idx + needle.length;
    const hit: number[] = [];
    for (let i = 0; i < items.length; i++) {
      const s = starts[i];
      const e = s + parts[i].length;
      if (parts[i] && e > idx && s < end) hit.push(i);
    }
    return hit.length ? hit : null;
  };

  const matchFragment = (frag: string): number[] | null => {
    const nf = sp(frag);
    if (nf.length < 12) return null;
    // Fast path: the (spaceless) quote or its leading run is a substring.
    const direct = findNeedle(nf) || findNeedle(nf.slice(0, Math.min(nf.length, 90)));
    if (direct) return direct;
    // Robust path for lightly-reworded quotes: union items covered by any
    // intact ~40-char window of the quote.
    const idxs = new Set<number>();
    const W = 40;
    for (let i = 0; i + W <= nf.length; i += 20) {
      const hit = findNeedle(nf.slice(i, i + W));
      if (hit) hit.forEach((x) => idxs.add(x));
    }
    return idxs.size ? Array.from(idxs) : null;
  };

  const frags = quote.split(/\.\.\.|…/).map((f) => f.trim()).filter((f) => f.length > 10);
  const all = new Set<number>();
  for (const frag of frags.length ? frags : [quote]) {
    const hit = matchFragment(frag);
    if (hit) hit.forEach((i) => all.add(i));
  }
  // Map sorted-order indices back to original item indices.
  return all.size ? Array.from(all).map((si) => orig[si]).sort((a, b) => a - b) : null;
}

function rectsForItems(items: any[], indices: number[], viewport: any, scale: number): Rect[] {
  return indices.map((i) => {
    const it = items[i];
    const tx = pdfjsLib.Util.transform(viewport.transform, it.transform);
    const fontHeight = Math.hypot(tx[2], tx[3]);
    return {
      left: tx[4],
      top: tx[5] - fontHeight,
      width: (it.width || 0) * scale,
      height: fontHeight * 1.15,
    };
  });
}

// ---------------------------------------------------------------------------
// One lazily-rendered page: renders its canvas + highlight rects the first
// time it scrolls near the viewport. Memoized so parent scroll state doesn't
// force re-renders.
// ---------------------------------------------------------------------------
interface PdfPageProps {
  pdf: any;
  pageNumber: number;
  scale: number;
  dims: { width: number; height: number };
  quotes: string[];
  rootRef: React.RefObject<HTMLDivElement>;
  setEl: (n: number, el: HTMLDivElement | null) => void;
  onHighlighted: (n: number, firstTop: number | null) => void;
}

const PdfPage: React.FC<PdfPageProps> = React.memo(
  ({ pdf, pageNumber, scale, dims, quotes, rootRef, setEl, onHighlighted }) => {
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const renderedRef = useRef(false);
    const [highlights, setHighlights] = useState<Rect[]>([]);

    const render = useCallback(async () => {
      if (renderedRef.current) return;
      renderedRef.current = true;
      const page = await pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: ctx, viewport }).promise;

      const tc = await page.getTextContent();
      const items = tc.items.filter((it: any) => typeof it.str === 'string') as any[];
      const rects: Rect[] = [];
      for (const q of quotes) {
        const m = matchItems(items, q);
        if (m) rects.push(...rectsForItems(items, m, viewport, scale));
      }
      setHighlights(rects);
      onHighlighted(pageNumber, rects.length ? Math.min(...rects.map((r) => r.top)) : null);
    }, [pdf, pageNumber, scale, quotes, onHighlighted]);

    useEffect(() => {
      const el = wrapRef.current;
      setEl(pageNumber, el);
      if (!el) return;
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            render();
            io.disconnect();
          }
        },
        { root: rootRef.current ?? null, rootMargin: '600px 0px' },
      );
      io.observe(el);
      return () => io.disconnect();
    }, [pageNumber, render, rootRef, setEl]);

    return (
      <div
        ref={wrapRef}
        data-page={pageNumber}
        className="relative mx-auto bg-white shadow-sm"
        style={{ width: dims.width, height: dims.height }}
      >
        <canvas ref={canvasRef} className="block" />
        {highlights.map((h, i) => (
          <div
            key={i}
            className="pointer-events-none absolute rounded-sm bg-yellow-300/55 mix-blend-multiply ring-1 ring-yellow-500/70"
            style={{ left: h.left, top: h.top, width: h.width, height: h.height }}
          />
        ))}
      </div>
    );
  },
);
PdfPage.displayName = 'PdfPage';

const DocViewerPanel: React.FC<DocViewerPanelProps> = ({ docket, citation, segments, onClose }) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pdfRef = useRef<any>(null);
  const pdfPathRef = useRef<string | null>(null);
  const pageEls = useRef<(HTMLDivElement | null)[]>([]);
  const scrolledRef = useRef(false);

  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  const [dims, setDims] = useState<{ width: number; height: number }[]>([]);
  const [pageNum, setPageNum] = useState(1);

  // Quotes to highlight, anchored to the page each was attributed to (the
  // primary source on its page, each additional passage on its page). This
  // uses the corrected page attribution instead of scanning every page,
  // which avoids a reworded quote matching a coincidental run elsewhere.
  // Quotes with no page fall back to a search on every page.
  const perPageQuotes = useMemo(() => {
    const byPage = new Map<number, string[]>();
    const unpaged: string[] = [];
    const add = (text: string, page: number | null) => {
      if (!text || !text.trim()) return;
      if (page == null) unpaged.push(text);
      else byPage.set(page, [...(byPage.get(page) ?? []), text]);
    };
    if (citation) {
      add(citation.sourceText, citation.page);
      citation.additionalQuotes.forEach((q) => add(q.text, q.page));
    }
    const arr: string[][] = [];
    for (let n = 1; n <= dims.length; n++) arr.push([...(byPage.get(n) ?? []), ...unpaged]);
    return arr;
  }, [citation, dims.length]);

  const setEl = useCallback((n: number, el: HTMLDivElement | null) => {
    pageEls.current[n - 1] = el;
  }, []);

  // When the page holding the primary citation has computed its highlight,
  // scroll it into view (once).
  const onHighlighted = useCallback(
    (n: number, firstTop: number | null) => {
      if (scrolledRef.current || !citation) return;
      const target = citation.page ?? 1;
      if (n !== target) return;
      const el = pageEls.current[n - 1];
      const container = scrollRef.current;
      if (!el || !container) return;
      scrolledRef.current = true;
      const offset = el.offsetTop + (firstTop != null ? firstTop - container.clientHeight / 3 : 0);
      container.scrollTo({ top: Math.max(0, offset), behavior: 'auto' });
    },
    [citation],
  );

  useEffect(() => {
    if (!citation) return;
    let cancelled = false;
    scrolledRef.current = false;
    (async () => {
      try {
        setStatus('loading');
        setError(null);
        const path = await resolveFilerPdfPath(docket, citation.entity, segments);
        if (!path) throw new Error(`Couldn't find the source document for ${citation.entity}.`);
        if (pdfPathRef.current !== path || !pdfRef.current) {
          const bytes = await downloadPdfBytes(path);
          if (cancelled) return;
          const pdf = await pdfjsLib.getDocument({ data: bytes.slice(0) }).promise;
          if (cancelled) return;
          pdfRef.current = pdf;
          pdfPathRef.current = path;
        }
        const pdf = pdfRef.current;
        const container = scrollRef.current;
        const containerWidth = (container?.clientWidth ?? 600) - 24;
        const first = await pdf.getPage(1);
        const s = Math.max(0.5, Math.min(2.2, containerWidth / first.getViewport({ scale: 1 }).width));
        const d: { width: number; height: number }[] = [];
        for (let n = 1; n <= pdf.numPages; n++) {
          const vp = (await pdf.getPage(n)).getViewport({ scale: s });
          d.push({ width: vp.width, height: vp.height });
        }
        if (cancelled) return;
        pageEls.current = new Array(pdf.numPages).fill(null);
        setScale(s);
        setDims(d);
        setPageNum(citation.page ?? 1);
        setStatus('ready');
        // Pre-position near the primary page so its lazy render fires; the
        // onHighlighted callback refines once the highlight is known.
        requestAnimationFrame(() => {
          const c = scrollRef.current;
          if (!c) return;
          const target = (citation.page ?? 1) - 1;
          const approx = d.slice(0, target).reduce((a, x) => a + x.height + 8, 0);
          c.scrollTop = approx;
        });
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load document');
          setStatus('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [docket, citation, segments]);

  // Live page indicator from scroll position.
  const onScroll = useCallback(() => {
    const c = scrollRef.current;
    if (!c || !dims.length) return;
    const mid = c.scrollTop + 80;
    let acc = 0;
    for (let i = 0; i < dims.length; i++) {
      acc += dims[i].height + 8;
      if (mid < acc) {
        setPageNum(i + 1);
        return;
      }
    }
    setPageNum(dims.length);
  }, [dims]);

  const goToPage = (n: number) => {
    const el = pageEls.current[n - 1];
    if (el && scrollRef.current) {
      scrollRef.current.scrollTo({ top: el.offsetTop, behavior: 'smooth' });
    }
  };

  // Accessibility: the document renders to <canvas>, which is invisible to
  // screen readers. We move focus to the panel heading when it opens and
  // announce status; the grounded passage is exposed as real text below
  // (see the sr-only block) so a blind user reads the exact supporting quote.
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => {
    if (!citation) return;
    if (status === 'ready') {
      headingRef.current?.focus();
      announce(
        `Source document for ${citation.entity} loaded` +
          (citation.page ? `, cited on page ${citation.page}` : ''),
      );
    } else if (status === 'error') {
      announce(error || 'Could not load the source document', { assertive: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (!citation) return null;
  const numPages = dims.length;

  return (
    <section
      className="flex h-full flex-col border-l border-gray-200 bg-gray-50"
      aria-labelledby={headingId}
    >
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-2.5">
        <div className="min-w-0">
          <h3
            id={headingId}
            ref={headingRef}
            tabIndex={-1}
            className="flex items-center gap-2 text-sm font-semibold text-gray-900 outline-none"
          >
            <FileText className="h-4 w-4 text-emerald-600 shrink-0" aria-hidden="true" />
            <span className="truncate">Source: {citation.entity}</span>
          </h3>
          <p className="truncate text-xs text-gray-500">{citation.topic}</p>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Close document viewer"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/* Screen-reader text alternative for the canvas: the exact grounded
          passage(s), as real text. This is what a blind user reads instead
          of the (inaccessible) rendered PDF image. */}
      <div className="sr-only">
        <p>
          Supporting passage from {citation.entity}
          {citation.page ? `, page ${citation.page}` : ''}:
        </p>
        <blockquote>{citation.sourceText}</blockquote>
        {citation.additionalQuotes?.map((q, i) => (
          <blockquote key={i}>
            Additional supporting passage{q.page ? `, page ${q.page}` : ''}: {q.text}
          </blockquote>
        ))}
      </div>

      {status === 'ready' && numPages > 0 && (
        <div className="flex items-center justify-center gap-3 border-b border-gray-200 bg-white px-3 py-1.5 text-sm">
          <button
            onClick={() => goToPage(Math.max(1, pageNum - 1))}
            disabled={pageNum <= 1}
            aria-label="Previous page"
            className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
          >
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          </button>
          <span className="tabular-nums text-gray-600">Page {pageNum} / {numPages}</span>
          <button
            onClick={() => goToPage(Math.min(numPages, pageNum + 1))}
            disabled={pageNum >= numPages}
            aria-label="Next page"
            className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
          >
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* The rendered PDF is decorative for assistive tech — the passage text
          above conveys the content. Hide the canvas region from screen
          readers so it isn't announced as an empty image. */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        aria-hidden="true"
        className="relative flex-1 overflow-auto p-3"
      >
        {status === 'loading' && (
          <div className="flex h-full items-center justify-center">
            <Loader className="h-5 w-5 animate-spin text-emerald-500" />
            <span className="ml-2 text-sm text-gray-500">Loading document…</span>
          </div>
        )}
        {status === 'error' && (
          <div className="flex h-full items-start justify-center pt-10">
            <div className="flex max-w-xs items-start gap-2 text-sm text-gray-600">
              <AlertCircle className="h-5 w-5 shrink-0 text-amber-500" />
              <span>{error}</span>
            </div>
          </div>
        )}
        {status === 'ready' && (
          <div className="flex flex-col items-center gap-2">
            {dims.map((d, i) => (
              <PdfPage
                key={i}
                pdf={pdfRef.current}
                pageNumber={i + 1}
                scale={scale}
                dims={d}
                quotes={perPageQuotes[i] ?? EMPTY_QUOTES}
                rootRef={scrollRef}
                setEl={setEl}
                onHighlighted={onHighlighted}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default DocViewerPanel;
